// Modal masowego ustawiania rabatu (procentowego lub wartościowego) dla zaznaczonych pozycji oferty.

import React, { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';

import { WiseModal } from 'components/wise';

interface IBulkSetDiscountModalProps {
  open: boolean;
  count: number;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (mode: 'pct' | 'value', value: number) => void;
}

const BulkSetDiscountModal: FC<IBulkSetDiscountModalProps> = ({
  open,
  count,
  isSubmitting,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'pct' | 'value'>('pct');
  const [valueStr, setValueStr] = useState<string>('0');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode('pct');
      setValueStr('0');
      setErrorMessage(null);
    }
  }, [open]);

  const handleSubmit = () => {
    const trimmed = valueStr.trim().replace(',', '.');
    if (trimmed === '') {
      setErrorMessage(t('Wprowadź wartość rabatu.'));
      return;
    }

    if (mode === 'pct') {
      const parsed = parseInt(trimmed, 10);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
        setErrorMessage(t('Nieprawidłowa wartość. Wprowadź liczbę z zakresu 0-100.'));
        return;
      }
      setErrorMessage(null);
      onSubmit('pct', parsed);
      return;
    }

    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      setErrorMessage(t('Nieprawidłowa wartość. Wprowadź liczbę nieujemną.'));
      return;
    }
    setErrorMessage(null);
    onSubmit('value', parsed);
  };

  return (
    <WiseModal
      open={open}
      onClose={onClose}
      title={t('Ustaw rabat dla zaznaczonych pozycji')}
      maxWidth={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 4 }}>
        <div>
          {t('Liczba zaznaczonych pozycji: {{count}}', { count })}
        </div>

        <FormControl>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as 'pct' | 'value')}>
            <FormControlLabel
              value="pct"
              control={
                <Radio
                  sx={{ color: '#6b54ff', '&.Mui-checked': { color: '#6b54ff' } }}
                />
              }
              label={t('Rabat procentowy')}
            />
            <FormControlLabel
              value="value"
              control={
                <Radio
                  sx={{ color: '#6b54ff', '&.Mui-checked': { color: '#6b54ff' } }}
                />
              }
              label={t('Rabat wartościowy')}
            />
          </RadioGroup>
        </FormControl>

        <TextField
          label={mode === 'pct' ? t('Rabat (%)') : t('Rabat (zł)')}
          value={valueStr}
          onChange={(e) => setValueStr(e.target.value)}
          type="number"
          inputProps={{ min: 0, max: mode === 'pct' ? 100 : undefined, step: mode === 'pct' ? 1 : 0.01 }}
          fullWidth
          autoFocus
        />

        {errorMessage && (
          <div style={{ color: '#e02e4e', fontSize: 13 }}>{errorMessage}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose} variant="text" disabled={isSubmitting}>
            {t('Anuluj')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isSubmitting ? <CircularProgress size={18} /> : t('Zastosuj')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default BulkSetDiscountModal;
